import { useEffect, useRef } from "react";
import { Modal, Typography, Space } from "antd";
import {
	ReloadOutlined,
	RocketOutlined,
	CheckCircleOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

const VERSION_STORAGE_KEY = "contabilidade_front_build_hash";
const VERSION_CHECK_INTERVAL = 2 * 60 * 1000;
let modalShown = false;

function buildHashUrl() {
	const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
	return `${base}/build-hash.json`;
}

const useVersionChecker = () => {
	const intervalRef = useRef(null);
	const isCheckingRef = useRef(false);

	const handleReload = () => {
		if ("caches" in window) {
			caches.keys().then((names) => {
				names.forEach((name) => {
					caches.delete(name);
				});
			});
		}
		window.location.reload();
	};

	const showUpdateNotification = () => {
		if (modalShown) {
			return;
		}

		modalShown = true;
		Modal.confirm({
			title: (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 12,
						marginBottom: 8,
					}}
				>
					<div
						style={{
							width: 48,
							height: 48,
							borderRadius: "50%",
							background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: 24,
							color: "white",
						}}
					>
						<RocketOutlined />
					</div>
					<div>
						<Title level={4} style={{ margin: 0 }}>
							Nova versão disponível
						</Title>
						<Text type="secondary" style={{ fontSize: 13 }}>
							Atualização pronta para instalar
						</Text>
					</div>
				</div>
			),
			icon: null,
			content: (
				<div style={{ padding: "16px 0", marginTop: 8 }}>
					<Space direction="vertical" size="middle" style={{ width: "100%" }}>
						<Paragraph style={{ margin: 0, fontSize: 15 }}>
							Uma nova versão da aplicação está disponível com melhorias e
							correções.
						</Paragraph>
						<div
							style={{
								background: "#e6f7ff",
								borderRadius: 8,
								padding: 16,
								border: "1px solid #91d5ff",
							}}
						>
							<Space>
								<CheckCircleOutlined
									style={{ color: "#1890ff", fontSize: 18 }}
								/>
								<Text
									style={{
										color: "#0050b3",
										fontSize: 14,
										fontWeight: 500,
									}}
								>
									Recomendamos atualizar agora para ter acesso às últimas
									melhorias
								</Text>
							</Space>
						</div>
					</Space>
				</div>
			),
			okText: (
				<Space>
					<ReloadOutlined />
					<span>Atualizar agora</span>
				</Space>
			),
			okButtonProps: {
				type: "primary",
				size: "large",
			},
			cancelText: "Mais tarde",
			cancelButtonProps: {
				size: "large",
			},
			onOk: handleReload,
			onCancel: () => {
				modalShown = false;
			},
			width: 540,
			centered: true,
			bodyStyle: { paddingTop: 8 },
			maskStyle: {
				backdropFilter: "blur(4px)",
				backgroundColor: "rgba(0, 0, 0, 0.45)",
			},
		});
	};

	const checkVersion = async () => {
		if (isCheckingRef.current) {
			return;
		}

		try {
			isCheckingRef.current = true;

			const url = `${buildHashUrl()}?t=${Date.now()}`;
			const response = await fetch(url, {
				method: "GET",
				cache: "no-cache",
			});

			if (!response.ok) {
				return;
			}

			const buildInfo = await response.json();
			const serverHash = buildInfo?.hash;

			if (!serverHash) {
				return;
			}

			const storedHash = localStorage.getItem(VERSION_STORAGE_KEY);

			if (!storedHash) {
				localStorage.setItem(VERSION_STORAGE_KEY, serverHash);
				return;
			}

			if (serverHash !== storedHash) {
				localStorage.setItem(VERSION_STORAGE_KEY, serverHash);
				showUpdateNotification();
			}
		} catch (error) {
			console.debug("Erro ao verificar versão:", error);
		} finally {
			isCheckingRef.current = false;
		}
	};

	useEffect(() => {
		checkVersion();

		intervalRef.current = setInterval(() => {
			checkVersion();
		}, VERSION_CHECK_INTERVAL);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};

export default useVersionChecker;
